import * as THREE from "three";
import { gsap } from "gsap";
import Experience from "./Experience.js";
import settings from "./settings.js";

export default class Camera {
  constructor() {
    this.experience = new Experience();
    this.sizes = this.experience.sizes;
    this.scene = this.experience.scene;
    this.canvas = this.experience.canvas;
    this.debug = this.experience.debug;

    // Debug
    if (this.debug.active) {
      this.debugFolder = this.debug.ui.addFolder("Camera");
    }
    this.setInstance();

    // Panels.closePanel announces closes; glide back to where we were
    window.addEventListener("panelclose", () => this.flyBack());
  }

  setInstance() {
    this.instance = new THREE.PerspectiveCamera(
      35,
      this.sizes.width / this.sizes.height,
      0.01,
      100,
    );
    this.instance.position.set(...settings.camera.position);
    this.scene.add(this.instance);

    if (this.debug.active) {
      this.debugFolder
        .add(this.instance, "fov")
        .name("cameraFov")
        .min(0)
        .max(100)
        .step(0.5)
        .onChange(() => {
          this.instance.updateProjectionMatrix(); // Ensure projection matrix is updated when FOV changes
        });
      this.debugFolder
        .add(this.instance.position, "z")
        .name("cameraZ")
        .min(-10)
        .max(20)
        .step(0.01)
        .onChange(() => {
          this.instance.updateProjectionMatrix(); // Ensure projection matrix is updated when FOV changes
        });
      this.debugFolder
        .add(this.instance.position, "y")
        .name("cameraY")
        .min(-10)
        .max(20)
        .step(0.01)
        .onChange(() => {
          this.instance.updateProjectionMatrix(); // Ensure projection matrix is updated when FOV changes
        });
      this.debugFolder
        .add(this.instance.position, "x")
        .name("cameraX")
        .min(-10)
        .max(20)
        .step(0.01)
        .onChange(() => {
          this.instance.updateProjectionMatrix(); // Ensure projection matrix is updated when FOV changes
        });
    }
  }

  resize() {
    this.instance.aspect = this.sizes.width / this.sizes.height;
    this.instance.updateProjectionMatrix();
  }
  scroll() {
    this.instance.position.y = this.restingPosition().y;
  }

  // Where the camera sits for the current scroll position
  restingPosition() {
    const [x, y, z] = settings.camera.position;
    const scrollOffset =
      (this.sizes.scrollY / this.sizes.height) * settings.camera.scrollFactor;
    return new THREE.Vector3(x, y - scrollOffset, z);
  }
  // Glide to `object`: end centred on it, moved `approach` of the way in,
  // calling `onArrive` shortly before the camera stops (so the panel fades
  // in as it settles)
  flyTo(object, onArrive) {
    const config = settings.flyTo;
    if (!config) {
      onArrive();
      return;
    }

    // Aim at the object's visual centre: some meshes (the Projects screen, the
    // envelope sheets) have their pivot at the room model's origin instead
    const target = new THREE.Box3()
      .setFromObject(object)
      .getCenter(new THREE.Vector3());
    const from = this.instance.position.clone();
    const rest = this.restingPosition();
    // Centre on the object in x/y, however far away it is (the menu can open
    // an object that's off-screen), and move part of the way in on z
    const to = new THREE.Vector3(
      target.x,
      target.y,
      THREE.MathUtils.lerp(rest.z, target.z, config.approach),
    );
    this.clampToWall(to);
    // Longer trips get a little more time
    const duration = Math.min(
      config.duration + from.distanceTo(to) * 0.08,
      config.maxDuration,
    );
    this.flownAway = true;

    this.flying = true;
    gsap.killTweensOf(this.instance.position);
    gsap.to(this.instance.position, {
      x: to.x,
      y: to.y,
      z: to.z,
      duration,
      ease: "power2.inOut",
      onComplete: () => {
        this.flying = false;
      },
    });
    // A plain timer, not gsap.delayedCall: GSAP's lag smoothing slows its clock
    // on slow frames, and the panel shouldn't wait on the frame rate
    window.setTimeout(onArrive, duration * 0.6 * 1000);
  }

  // Keep the view inside the back wall, so centring on an object near the
  // edge (e.g. the Experience picture) doesn't show past the end of the room
  clampToWall(position) {
    const wall = this.experience.world?.room?.wall;
    if (!wall) return;

    this.wallEdges ??= this.measureWall(wall);
    const { left, right, top, z } = this.wallEdges;
    // How much of the wall is visible from this position
    const halfHeight =
      Math.tan(THREE.MathUtils.degToRad(this.instance.fov / 2)) *
      (position.z - z);
    const halfWidth = halfHeight * this.instance.aspect;
    const clamp = (value, low, high) =>
      low > high ? (low + high) / 2 : THREE.MathUtils.clamp(value, low, high);

    position.x = clamp(position.x, left + halfWidth, right - halfWidth);
    // Only the top: at the bottom the floor fills the view, and clamping there
    // would lift the camera away from floor objects like the envelope
    position.y = Math.min(position.y, top - halfHeight);
  }

  // The wall mesh (Room_1) also contains other parts of the room, so its
  // bounding box is wider than the wallpaper. Find the wallpaper's real edges
  // by casting rays at it and binary-searching where they stop hitting.
  measureWall(wall) {
    const box = new THREE.Box3().setFromObject(wall);
    const raycaster = new THREE.Raycaster();
    const back = new THREE.Vector3(0, 0, -1);
    // The back wall is the rearmost forward-facing surface of the mesh
    const hitWall = (x, y) => {
      raycaster.set(new THREE.Vector3(x, y, box.max.z + 1), back);
      const hits = raycaster
        .intersectObject(wall)
        .filter(
          (hit) =>
            hit.face.normal.clone().transformDirection(hit.object.matrixWorld)
              .z > 0.9,
        );
      return hits.length ? hits[hits.length - 1].point.z : null;
    };
    const edge = (inside, outside, probe) => {
      for (let i = 0; i < 24; i++) {
        const middle = (inside + outside) / 2;
        if (probe(middle) === null) outside = middle;
        else inside = middle;
      }
      return inside;
    };

    const z = hitWall(0, 0);
    return {
      z,
      left: edge(0, box.min.x - 1, (x) => hitWall(x, 0)),
      right: edge(0, box.max.x + 1, (x) => hitWall(x, 0)),
      top: edge(0, box.max.y + 1, (y) => hitWall(0, y)),
    };
  }

  flyBack() {
    if (!this.flownAway) return;

    this.flownAway = false;
    const home = this.restingPosition();
    const config = settings.flyTo;
    const duration = Math.min(
      config.duration + this.instance.position.distanceTo(home) * 0.08,
      config.maxDuration,
    );
    gsap.killTweensOf(this.instance.position);
    gsap.to(this.instance.position, {
      x: home.x,
      y: home.y,
      z: home.z,
      duration,
      ease: "power2.inOut",
      // Re-sync with the scroll position in case the page scrolled meanwhile
      onComplete: () => this.scroll(),
    });
  }

  update() {}
}
